<template>
  <div class="external-app-page">
    <!-- 搜索栏 -->
    <div class="search-bar">
      <el-form :inline="true" :model="queryParams">
        <el-form-item label="应用名称">
          <el-input v-model="queryParams.appName" placeholder="请输入应用名称" clearable @keyup.enter.native="handleSearch" />
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="queryParams.status" placeholder="请选择" clearable>
            <el-option label="启用" :value="1" />
            <el-option label="禁用" :value="0" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
          <el-button icon="el-icon-refresh" @click="resetQuery">重置</el-button>
          <el-button type="success" icon="el-icon-plus" @click="handleAdd">新增</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- 列表 -->
    <el-table v-loading="loading" :data="tableData" border stripe>
      <el-table-column prop="id" label="ID" width="60" align="center" />
      <el-table-column prop="appName" label="应用名称" min-width="140" />
      <el-table-column prop="appKey" label="AppKey" min-width="240" show-overflow-tooltip />
      <el-table-column label="AppSecret" min-width="200" align="center">
        <template slot-scope="{ row }">
          <span v-if="!row._showSecret && !row._loadingSecret">{{ maskSecret(row.appSecret) }}</span>
          <span v-else-if="row._loadingSecret" style="color: #909399;">加载中...</span>
          <span v-else style="font-size: 12px; word-break: break-all;">{{ row.appSecret }}</span>
          <template v-if="isAdmin">
            <el-button type="text" size="mini" @click="toggleSecret(row)" :loading="row._loadingSecret" style="margin-left: 4px;">
              {{ row._showSecret ? '隐藏' : '查看' }}
            </el-button>
            <el-button type="text" size="mini" @click="copySecret(row)">复制</el-button>
          </template>
        </template>
      </el-table-column>
      <el-table-column prop="status" label="状态" width="80" align="center">
        <template slot-scope="{ row }">
          <el-tag :type="row.status === 1 ? 'success' : 'danger'" size="small">
            {{ row.status === 1 ? '启用' : '禁用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="总调用次数" width="100" align="center">
        <template slot-scope="{ row }">
          {{ row.totalCallCount || 0 }}
        </template>
      </el-table-column>
      <el-table-column label="今日" width="70" align="center">
        <template slot-scope="{ row }">
          {{ row.todayCallCount || 0 }}
        </template>
      </el-table-column>
      <el-table-column label="昨日" width="70" align="center">
        <template slot-scope="{ row }">
          {{ row.yesterdayCallCount || 0 }}
        </template>
      </el-table-column>
      <el-table-column label="前日" width="70" align="center">
        <template slot-scope="{ row }">
          {{ row.dayBeforeYesterdayCallCount || 0 }}
        </template>
      </el-table-column>
      <el-table-column prop="remark" label="备注" min-width="160" show-overflow-tooltip />
      <el-table-column prop="createTime" label="创建时间" width="180" align="center">
        <template slot-scope="{ row }">
          {{ formatTime(row.createTime) }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="280" align="center" fixed="right">
        <template slot-scope="{ row }">
          <el-button type="text" size="small" @click="handleEdit(row)">编辑</el-button>
          <el-button type="text" size="small" @click="handleToggleStatus(row)">
            {{ row.status === 1 ? '禁用' : '启用' }}
          </el-button>
          <el-button type="text" size="small" @click="handleResetSecret(row)">重置密钥</el-button>
          <el-button type="text" size="small" style="color: #f56c6c;" @click="handleDelete(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination-wrapper">
      <el-pagination
        background
        layout="total, prev, pager, next, sizes"
        :total="total"
        :page-sizes="[10, 20, 50, 100]"
        :page-size.sync="queryParams.pageSize"
        :current-page.sync="queryParams.pageNo"
        @size-change="loadData"
        @current-change="loadData"
      />
    </div>

    <!-- 新增/编辑弹窗 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="600px" append-to-body :close-on-click-modal="false">
      <el-form ref="formRef" :model="form" :rules="rules" label-width="100px">
        <el-form-item label="应用名称" prop="appName">
          <el-input v-model="form.appName" placeholder="请输入应用名称" maxlength="128" />
        </el-form-item>
        <el-form-item label="AppKey" prop="appKey">
          <el-input v-model="form.appKey" placeholder="请输入AppKey（唯一标识）" maxlength="64" :disabled="!!form.id" />
        </el-form-item>
        <el-form-item label="AppSecret" prop="appSecret">
          <el-input v-model="form.appSecret" placeholder="请输入AppSecret（留空则自动生成）" maxlength="64" show-password />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="form.remark" placeholder="请输入备注" maxlength="256" type="textarea" :rows="2" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" :loading="submitLoading" @click="handleSubmit">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 重置密钥确认弹窗 -->
    <el-dialog title="重置密钥" :visible.sync="resetSecretVisible" width="450px" append-to-body>
      <div style="padding: 10px 0;">
        <p style="color: #e6a23c; margin-bottom: 12px;"><i class="el-icon-warning"></i> 重置后旧密钥将立即失效，请确保调用方已更新配置。</p>
        <p v-if="newSecret" style="margin-top: 16px;">
          新密钥：<code style="background:#f5f7fa; padding: 4px 8px; border-radius: 4px; user-select: all;">{{ newSecret }}</code>
        </p>
      </div>
      <div slot="footer">
        <el-button @click="resetSecretVisible = false">关 闭</el-button>
        <el-button v-if="!newSecret" type="primary" @click="confirmResetSecret">确认重置</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getExternalAppList, getExternalAppDetail, addExternalApp, updateExternalApp, deleteExternalApp, toggleExternalAppStatus, resetExternalAppSecret } from '@/api/externalApp'
import { mapState } from 'vuex'

export default {
  name: 'ExternalApp',
  data() {
    return {
      loading: false,
      tableData: [],
      total: 0,
      queryParams: { appName: null, status: null, pageNo: 1, pageSize: 10 },
      dialogVisible: false,
      dialogTitle: '新增应用',
      submitLoading: false,
      form: { id: null, appName: '', appKey: '', appSecret: '', remark: '' },
      rules: {
        appName: [{ required: true, message: '请输入应用名称', trigger: 'blur' }],
        appKey: [{ required: true, message: '请输入AppKey', trigger: 'blur' }]
      },
      resetSecretVisible: false,
      resetSecretId: null,
      newSecret: ''
    }
  },
  created() {
    this.loadData()
  },
  computed: {
    ...mapState('user', ['permissions']),
    isAdmin() {
      return (this.permissions || []).includes('externalApp:viewSecret')
    }
  },
  methods: {
    loadData() {
      this.loading = true
      getExternalAppList(this.queryParams).then(res => {
        this.tableData = (res.data && res.data.list) || []
        this.total = (res.data && res.data.total) || 0
      }).finally(() => { this.loading = false })
    },
    handleSearch() {
      this.queryParams.pageNo = 1
      this.loadData()
    },
    resetQuery() {
      this.queryParams = { appName: null, status: null, pageNo: 1, pageSize: 10 }
      this.loadData()
    },
    handleAdd() {
      this.dialogTitle = '新增应用'
      this.form = { id: null, appName: '', appKey: '', appSecret: '', remark: '' }
      this.dialogVisible = true
    },
    handleEdit(row) {
      this.dialogTitle = '编辑应用'
      this.form = { ...row }
      this.dialogVisible = true
    },
    handleSubmit() {
      this.$refs.formRef.validate(valid => {
        if (!valid) return
        this.submitLoading = true
        const api = this.form.id ? updateExternalApp : addExternalApp
        api(this.form).then(res => {
          if (res.code === 200) {
            this.$message.success(this.form.id ? '编辑成功' : '新增成功')
            this.dialogVisible = false
            this.loadData()
          } else {
            this.$message.error(res.message || '操作失败')
          }
        }).finally(() => { this.submitLoading = false })
      })
    },
    handleDelete(row) {
      this.$confirm('确认删除应用"' + row.appName + '"？', '提示', { type: 'warning' }).then(() => {
        deleteExternalApp(row.id).then(res => {
          if (res.code === 200) {
            this.$message.success('删除成功')
            this.loadData()
          } else {
            this.$message.error(res.message || '删除失败')
          }
        })
      }).catch(() => {})
    },
    handleToggleStatus(row) {
      const action = row.status === 1 ? '禁用' : '启用'
      this.$confirm('确认' + action + '应用"' + row.appName + '"？', '提示', { type: 'warning' }).then(() => {
        toggleExternalAppStatus(row.id).then(res => {
          if (res.code === 200) {
            this.$message.success(action + '成功')
            this.loadData()
          } else {
            this.$message.error(res.message || action + '失败')
          }
        })
      }).catch(() => {})
    },
    handleResetSecret(row) {
      this.resetSecretId = row.id
      this.newSecret = ''
      this.resetSecretVisible = true
    },
    confirmResetSecret() {
      resetExternalAppSecret(this.resetSecretId).then(res => {
        if (res.code === 200) {
          this.newSecret = res.data
          this.$message.success('密钥已重置，请妥善保存')
          this.loadData()
        } else {
          this.$message.error(res.message || '重置失败')
        }
      })
    },
    toggleSecret(row) {
      if (row._showSecret) {
        // 已展开，点击隐藏
        this.$set(row, '_showSecret', false)
        return
      }
      // 展开：从后端获取真实 appSecret
      this.$set(row, '_loadingSecret', true)
      getExternalAppDetail(row.id).then(res => {
        if (res.code === 200 && res.data && res.data.appSecret) {
          this.$set(row, 'appSecret', res.data.appSecret)
          this.$set(row, '_showSecret', true)
        } else {
          this.$message.error('获取密钥失败')
        }
      }).catch(() => {
        this.$message.error('获取密钥异常')
      }).finally(() => {
        this.$set(row, '_loadingSecret', false)
      })
    },
    copySecret(row) {
      // 从后端获取真实 appSecret 并复制
      getExternalAppDetail(row.id).then(res => {
        if (res.code === 200 && res.data && res.data.appSecret) {
          const val = res.data.appSecret
          const input = document.createElement('input')
          input.value = val
          document.body.appendChild(input)
          input.select()
          document.execCommand('copy')
          document.body.removeChild(input)
          this.$message.success('已复制到剪贴板')
        } else {
          this.$message.error('获取密钥失败')
        }
      }).catch(() => {
        this.$message.error('获取密钥异常')
      })
    },
    maskSecret(val) {
      if (!val) return '-'
      if (val.length <= 8) return '****'
      return val.substring(0, 4) + '****' + val.substring(val.length - 4)
    },
    formatTime(val) {
      if (!val) return '-'
      return val.replace('T', ' ')
    }
  }
}
</script>

<style lang="less" scoped>
.external-app-page {
  padding: 20px;
}
.search-bar {
  background: #fff;
  padding: 18px 18px 0;
  margin-bottom: 16px;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
}
.pagination-wrapper {
  margin-top: 16px;
  text-align: right;
}
</style>
